/**
 * Navigation 계층: roving primary tab stop, ↑/↓/Home/End 탐색(selection-follows-focus),
 * off-DOM 행으로의 pending focus 전달.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";

import type { ListViewEntry } from "./useListViewCollections";
import type { ListViewSelect } from "./useListViewSelection";

export function useListViewNavigation<T>({
  navigationEntries,
  selectedNavigationIndex,
  selectedEntry,
  keyboardNavigation,
  select,
  virtualized,
  triggerIndex,
  requestLoadMore,
  scrollVirtualIndexIntoView,
  scrollMountedItemIntoView,
}: {
  navigationEntries: readonly ListViewEntry<T>[];
  selectedNavigationIndex: number;
  selectedEntry: ListViewEntry<T> | undefined;
  keyboardNavigation: boolean;
  select: ListViewSelect<T>;
  virtualized: boolean;
  triggerIndex: number;
  requestLoadMore: () => void;
  scrollVirtualIndexIntoView: (index: number) => void;
  scrollMountedItemIntoView: (element: HTMLElement) => void;
}): {
  registerItem: (key: string, element: HTMLButtonElement | null) => void;
  handleItemKeyDown: (event: ReactKeyboardEvent<HTMLButtonElement>, globalIndex: number) => void;
} {
  const itemRefs = useRef(new Map<string, HTMLButtonElement>());
  const pendingFocusKeyRef = useRef<string | null>(null);

  const selectedEntryKey = selectedEntry?.key;
  const selectedEntryIndex = selectedEntry?.index;
  const selectedEntryPinned = selectedEntry?.pinned ?? false;

  function registerItem(key: string, element: HTMLButtonElement | null) {
    if (element) {
      itemRefs.current.set(key, element);
      return;
    }
    itemRefs.current.delete(key);
  }

  // scroll 함수는 viewport 실측값에 따라 identity가 바뀌므로 ref로 읽는다 — reveal은
  // 선택이 실제로 바뀔 때만 실행해야 하고, 뷰포트 크기 변화가 선택 행으로 되감으면 안 된다.
  const revealRef = useRef({ scrollVirtualIndexIntoView, scrollMountedItemIntoView });
  useEffect(() => {
    revealRef.current = { scrollVirtualIndexIntoView, scrollMountedItemIntoView };
  });

  // controlled 선택이 외부에서 바뀌어도 scroll을 맞추되 포커스는 훔치지 않는다.
  useEffect(() => {
    if (selectedEntryKey === undefined || selectedEntryIndex === undefined) return;
    const reveal = revealRef.current;
    if (virtualized && !selectedEntryPinned) {
      reveal.scrollVirtualIndexIntoView(selectedEntryIndex);
      return;
    }
    const element = itemRefs.current.get(selectedEntryKey);
    if (element) reveal.scrollMountedItemIntoView(element);
  }, [selectedEntryIndex, selectedEntryKey, selectedEntryPinned, virtualized]);

  useLayoutEffect(() => {
    const pendingKey = pendingFocusKeyRef.current;
    if (!pendingKey) return;
    // 탐색 대상에서 사라진 key는 폐기한다 — 잔류한 pending key가 한참 뒤 같은 key의
    // mount 순간 무관한 element에서 focus를 훔치는 것을 막는다.
    if (!navigationEntries.some((entry) => entry.key === pendingKey)) {
      pendingFocusKeyRef.current = null;
      return;
    }
    const element = itemRefs.current.get(pendingKey);
    if (!element) return;
    element.focus({ preventScroll: true });
    pendingFocusKeyRef.current = null;
  });

  function selectNavigationEntry(globalIndex: number, prefetchOnEnter: boolean, focus: boolean) {
    const entry = navigationEntries[globalIndex];
    if (!entry) return;
    if (!entry.pinned && prefetchOnEnter && entry.index >= triggerIndex) requestLoadMore();
    if (focus && virtualized && !entry.pinned) {
      pendingFocusKeyRef.current = entry.key;
      scrollVirtualIndexIntoView(entry.index);
    }
    select(entry, "navigation");
    if (focus) {
      const element = itemRefs.current.get(entry.key);
      if (element) {
        scrollMountedItemIntoView(element);
        element.focus({ preventScroll: true });
        pendingFocusKeyRef.current = null;
      } else {
        pendingFocusKeyRef.current = entry.key;
      }
    }
  }

  function handleItemKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>, globalIndex: number) {
    if (!keyboardNavigation) return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    // 화살표는 event target보다 controlled selectedKey를 기준으로 이동한다.
    const navigationBaseIndex =
      selectedNavigationIndex >= 0 ? selectedNavigationIndex : globalIndex;
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        selectNavigationEntry(
          Math.min(navigationEntries.length - 1, navigationBaseIndex + 1),
          true,
          true,
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        selectNavigationEntry(Math.max(0, navigationBaseIndex - 1), false, true);
        break;
      case "Home":
        event.preventDefault();
        selectNavigationEntry(0, false, true);
        break;
      case "End":
        event.preventDefault();
        selectNavigationEntry(navigationEntries.length - 1, true, true);
        break;
      default:
        break;
    }
  }

  return { registerItem, handleItemKeyDown };
}
